'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { Typography, Box } from '@mui/material';
import dayjs from 'dayjs';

import TireReceivingAdd from './TireReceivingAdd';
import TireReceivingEdit from './TireReceivingEdit';
import TireCardHistory from './TireCardHistory';
import { useLanguage } from '../../../utils/i18n';
import { useTireBatchList, deleteTireBatch, getTireBatchList } from '../../../stores/tireBatchStore';
import { useCompanyList } from '../../../stores/companyStore';
import { fleetApi } from '../../../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function TireReceivingPageContent() {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedSerialNo, setSelectedSerialNo] = useState(null);

  const handleViewHistory = (row) => {
    setSelectedSerialNo(row?.serial_number);
    setHistoryOpen(true);
  };

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('serial_number');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  // const filterCompanyIds = useMemo(() => {
  //   if (!selectedCompanies || selectedCompanies.length === 0) return '';
  //   const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
  //   if (hasAll) return '';
  //   return selectedCompanies.map((s) => String(s.value)).join(',');
  // }, [selectedCompanies]);

  const [localTires, setLocalTires] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'serial_number', label: t('tyreItem.serialNumber', 'Serial No') },
      { value: 'tire_item_batch_number', label: t('tireReceiving.batchNo', 'Batch No') },
      { value: 'tire_brand_name', label: t('brand.name', 'Brand') },
      { value: 'tire_size_code', label: t('size.tireSize', 'Size') },
      { value: 'tire_spec_name', label: t('specification.pattern', 'Pattern') },
      { value: 'receive_date', label: t('tireReceiving.receiveDate', 'Receive Date') },
      { value: 'supplier', label: t('tireReceiving.supplier', 'Supplier') },
      { value: 'base_condition', label: t('dashboardAsset.condition', 'Condition') },
      { value: 'tire_item_warehouse_name', label: t('warehouse.name', 'Warehouse') },
      { value: 'tire_item_total_price', label: t('tireReceiving.price', 'Price') }
    ],
    [t]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: hookData,
    totalRecord: hookTotal,
    isLoading: hookLoading,
    refetch
  } = useTireBatchList(paginationModel.page, paginationModel.pageSize, selectedCompanies, hookSearchField, hookSearchValue);

  const rows = searchField === 'all' ? localTires : hookData || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecord = searchField === 'all' ? localTotal : hookTotal || 0;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const columns = [];

        if (selectedCompanies && selectedCompanies.length > 0) {
          const values = selectedCompanies.map((s) => String(s.value));
          if (values.length === 1) {
            columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
          } else {
            columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
          }
        }

        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'serial_number', value: searchValue },
            { name: 'base_condition', value: searchValue },
            { name: 'tire_brand_name', table_name: 'tire_item', value: searchValue },
            { name: 'tire_size_code', table_name: 'tire_item', value: searchValue },
            { name: 'tire_size_code_internal', table_name: 'tire_item', value: searchValue },
            { name: 'tire_spec_name', table_name: 'tire_item', value: searchValue },
            { name: 'receive_date', table_name: 'tire_item', value: searchValue },
            { name: 'supplier', table_name: 'tire_item', value: searchValue },
            { name: 'batch_number', table_name: 'tire_item', value: searchValue },
            { name: 'warehouse_name', table_name: 'tire_item', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['created_at'], ascending: false },
          joins: [
            { name: 'company', column_results: ['name'] },
            {
              name: 'tire_item',
              column_results: [
                'name',
                'tire_brand_name',
                'tire_size_code',
                'tire_size_code_internal',
                'tire_spec_name',
                'status',
                'total_price',
                'receive_date',
                'supplier',
                'batch_number',
                'warehouse_name'
              ]
            },
            {
              name: 'm_tire_item.m_tire_spec',
              column_results: ['pattern', 'original_tread_depth', 'pressure_std'],
              relation: 'tire_spec_id',
              reference: 'id'
            }
          ],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/tire-batch/search' + getTzParam(), payload, { signal: controller.signal });
        const fetchedRows = Array.isArray(resp?.data) ? resp.data : [];
        const total = resp?.totalRecord ?? fetchedRows.length;

        setLocalTires(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('tire search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, selectedCompanies]);

  const handleFetchAllForExport = async () => {
    try {
      const activeSearchField = searchField === 'all' ? null : searchField;
      const allData = await getTireBatchList(selectedCompanies, activeSearchField, searchValue);
      let filtered = allData;
      if (searchValue && searchValue.trim() !== '') {
        const val = searchValue.toLowerCase();
        filtered = allData.filter((item) => {
          if (searchField === 'all') {
            return (
              String(item.serial_number || '')
                .toLowerCase()
                .includes(val) ||
              String(item.join?.tire_item_tire_brand_name || '')
                .toLowerCase()
                .includes(val) ||
              String(item.join?.tire_item_tire_size_code || '')
                .toLowerCase()
                .includes(val) ||
              String(item.join?.tire_item_supplier || '')
                .toLowerCase()
                .includes(val) ||
              String(item.current_location || '')
                .toLowerCase()
                .includes(val)
            );
          }

          if (searchField === 'tire_item_total_price') {
            return String(item.join?.tire_item_total_price || '')
              .toLowerCase()
              .includes(val);
          }

          return String(item[searchField] || item.join?.[`tire_item_${searchField}`] || '')
            .toLowerCase()
            .includes(val);
        });
      }
      return filtered;
    } catch (err) {
      console.error('tire receiving export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [secondConfirmOpen, setSecondConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmRequireInput, setConfirmRequireInput] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, row) => {
    if (row?.current_location === 'FIT') {
      notify({ message: 'Tire cannot be deleted because its current location is FIT.', severity: 'error' });
      return;
    }

    setConfirmTargetId(id);
    setConfirmName(row?.serial_number || '');
    setConfirmRequireInput(row?.current_location === 'REMOVE');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setSecondConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
    setConfirmRequireInput(false);
  };

  const handleConfirmDelete = async () => {
    if (confirmRequireInput && !secondConfirmOpen) {
      setConfirmOpen(false);
      setTimeout(() => setSecondConfirmOpen(true), 200);
      return;
    }

    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteTireBatch(confirmTargetId);
      notify({ message: t('tireReceiving.notifSuccess', 'Tire Receiving successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('tireReceiving.notifFailed', 'Failed to delete Tire Receiving'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setSecondConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
      setConfirmRequireInput(false);
    }
  };

  const columns = [
    {
      field: 'company_name',
      headerName: t('general.company', 'Company'),
      width: 100,
      valueGetter: (params, row) => row?.join?.company_name || row?.company_name || '-'
    },
    {
      field: 'serial_number',
      headerName: t('tyreItem.serialNumber', 'Serial No'),
      width: 150,
      valueGetter: (params, row) => row?.serial_number || '-'
    },
    {
      field: 'tire_item_batch_number',
      headerName: t('tireReceiving.batchNo', 'Batch No'),
      width: 150,
      valueGetter: (params, row) => row?.join?.tire_item_batch_number || '-'
    },
    {
      field: 'brand_and_size',
      headerName: t('tireReceiving.brandAndSize', 'Brand & Size'),
      width: 150,
      valueGetter: (params, row) => {
        const brand = row?.join?.tire_item_tire_brand_name || '-';
        const size = row?.join?.tire_item_tire_size_code || '-';
        return `${brand} - ${size}`;
      },
      renderCell: (params) => (
        <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
          <Typography variant="body2" fontWeight={600}>
            {params.row?.join?.tire_item_tire_brand_name || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {params.row?.join?.tire_item_tire_size_code || '-'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'tire_spec_name',
      headerName: t('specification.pattern', 'Pattern'),
      width: 100,
      valueGetter: (params, row) => row?.join?.tire_item_tire_spec_name || '-'
    },
    {
      field: 'receive_date',
      headerName: t('tireReceiving.receiveDate', 'Receive Date'),
      width: 150,
      valueGetter: (params, row) => {
        const val = row?.join?.tire_item_receive_date;
        return val ? dayjs(val).format('DD-MM-YYYY') : '-';
      }
    },
    {
      field: 'supplier',
      headerName: t('tireReceiving.supplier', 'Supplier'),
      width: 150,
      valueGetter: (params, row) => row?.join?.tire_item_supplier || '-'
    },
    {
      field: 'base_condition',
      headerName: t('dashboardAsset.condition', 'Receive Condition'),
      width: 100,
      valueGetter: (params, row) => row?.base_condition || '-'
    },
    {
      field: 'tire_item_warehouse_name',
      headerName: t('warehouse.name', 'Warehouse'),
      width: 150,
      valueGetter: (params, row) => row?.join?.tire_item_warehouse_name || '-'
    },
    {
      field: 'tire_item_total_price',
      headerName: t('tireReceiving.unitPrice', 'Unit Price'),
      width: 150,
      valueGetter: (params, row) => {
        const val = row?.join?.tire_item_total_price;
        return val ? `Rp. ${Number(val).toLocaleString('id-ID')}` : '-';
      }
    },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu
          row={params.row}
          onView={(id, row) => handleViewHistory(params.row)}
          onEdit={() => handleEditClick(params.row?.tire_item)}
          onDelete={!params.row?.current_location ? (id) => handleDelete(id, params.row) : undefined}
          mode="inline"
        />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('tireReceiving.title', 'Master Tire Receiving')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecord}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('tireReceiving.search', 'Search Tire Receiving')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={(newVals) => {
          setSelectedCompanies(newVals);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        rowHeight={90}
      />

      {openDialog && !editingId && <TireReceivingAdd open={openDialog} onClose={handleDialogClose} onSuccess={() => refetch()} />}
      {openDialog && editingId && (
        <TireReceivingEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />
      )}

      {historyOpen && selectedSerialNo && (
        <TireCardHistory
          open={historyOpen}
          onClose={() => {
            setHistoryOpen(false);
            setSelectedSerialNo(null);
          }}
          serialNumber={selectedSerialNo}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
        requireInputMatch={false}
        matchText={confirmName}
      />
      <DeleteConfirmDialog
        open={secondConfirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('tireReceiving.finalConfirm', 'Final Confirmation')}
        message={t(
          'tireReceiving.finalConfirmMessage',
          'This is a REMOVE tire. You must confirm by typing the serial number to permanently delete it.'
        )}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
        requireInputMatch={true}
        matchText={confirmName}
      />
    </div>
  );
}

export default function TireReceivingPage() {
  return <TireReceivingPageContent />;
}
